import { useIntl } from 'react-intl';
import { BoxContextProvider, BoxField } from '@stackhouseos/box-core';
import React, { useContext, useMemo, JSX } from 'react';
import { genericMessages } from '../../generic.i18n';
import {
	BoxComponentType,
	IBoxComponent,
	IBoxSubmitComponent,
	isSubmitComponent,
	IFormConfig,
} from '@enuan/chat__utils';
import { IntlMessage, useIntlMessage } from '../../hooks';
import { CustomBox, isInputComponent } from '../box/CustomBox';
import { FormInputs } from '../box/controls/FormInputs';
import { configContext } from '../../contexts/config/context';
import classNames from 'classnames';
import { useEnuanLocalStorage } from '../../hooks/useEnuanLocalStorage';
import { styled } from '@mui/material/styles';

export type IFormProps = Omit<JSX.IntrinsicElements['form'], 'action'> & {
	prefix: string;
	readOnly?: boolean;
	onSubmitAction?: (values: Record<string, any>) => boolean;
	messages?: IntlMessage;
	destroyValue?: boolean;
	formConfig: Omit<Partial<IFormConfig>, 'header'>;
};

export const Form = styled(
	({
		prefix,
		readOnly,
		messages,

		onSubmitAction,
		onSubmit,
		destroyValue,
		formConfig,
		className,
		...formProps
	}: IFormProps) => {
		const {
			rememberId,
			rememberDefault,
			enableLocalStorage,
			items: _items,
			rules,
		} = formConfig;
		const intl = useIntl();
		const getMessage = useIntlMessage(messages);
		const { setLockInput } = useContext(configContext);

		const { get, unset, set } = useEnuanLocalStorage();

		const storedAuthData = useMemo<Record<string, any>>(
			() => (rememberId && enableLocalStorage ? (get(rememberId) ?? {}) : {}),
			[get, rememberId, enableLocalStorage],
		);

		const items = useMemo(() => {
			return (
				_items?.map((item) => {
					const label = getMessage ? getMessage(item.label) : item.label;
					if (!isInputComponent(item)) return { ...item, label };

					const storedValue = item.id ? storedAuthData[item.id] : undefined;

					return {
						ruleModeDisable: true,
						...item,
						default: storedValue ?? item.default,
						rules: {
							and: [
								item.rules,
								{
									'^readOnly': { neq: true },
								},
							].filter(Boolean),
						},
					};
				}) ?? []
			);
		}, [_items, storedAuthData, getMessage]);

		const safeItems = useMemo(
			() =>
				items.reduce<string[]>(
					(prev, { id, remember }) =>
						remember !== false && id ? [...prev, id] : prev,
					[],
				),
			[items],
		);

		const { _fields, submit } = useMemo(
			() =>
				items.reduce<{
					_fields: typeof items;
					submit?: IBoxSubmitComponent;
				}>(
					(prev, curr) => {
						if (isSubmitComponent(curr)) {
							return { ...prev, submit: curr };
						}
						return { ...prev, _fields: [...prev._fields, curr] };
					},
					{ _fields: [], submit: undefined },
				),
			[items],
		);

		const inputs = useMemo<BoxField>(() => {
			return { type: FormInputs, fields: _fields };
		}, [_fields]);

		const newSubmit = useMemo<
			IBoxComponent<
				IBoxSubmitComponent<JSX.IntrinsicElements['button']>,
				Record<string, any>
			>
		>(
			() => ({
				type: BoxComponentType.SUBMIT,
				label: intl.formatMessage(genericMessages.send),
				rules: {
					and: [
						rules,
						{
							'^isValid': {
								eq: true,
							},
						},
						{
							'^readOnly': { neq: true },
						},
					].filter(Boolean),
				},
				ruleModeDisable: !readOnly,
				action: (data) => {
					const {
						payload: { value = {} },
					} = data;
					if (rememberId) {
						const safeData = Object.entries(value).reduce<Record<string, any>>(
							(prev, [key, value]) =>
								safeItems.includes(key) || key === rememberId
									? { ...prev, [key]: value }
									: prev,
							{},
						);
						if (safeData[rememberId] === true && enableLocalStorage) {
							set(rememberId, safeData, undefined);
						} else {
							unset(rememberId);
						}
					}

					const result = onSubmitAction?.(value);
					// TODO move outside on client component
					if (result) {
						setLockInput(false);
					}
					return result;
				},
				onClick: (e: React.SyntheticEvent) => {
					e.preventDefault();
				},
				...(submit ?? {}),
			}),
			[
				intl,
				rules,
				readOnly,
				rememberId,
				safeItems,
				enableLocalStorage,
				submit,
				onSubmitAction,
				set,
				unset,
				setLockInput,
			],
		);

		const submitFormRow = useMemo<BoxField>(() => {
			const rememberItem = rememberId
				? [
						{
							type: 'Checkbox',
							id: rememberId,
							default: rememberDefault ?? storedAuthData[rememberId],
							className: 'submit__remember_data',
							label:
								newSubmit.remember_label ??
								intl.formatMessage(genericMessages.remember_data),
							disabled: readOnly,
						},
						{
							type: BoxComponentType.DIV,
							className: 'spacer',
						},
					]
				: [];

			const newSubmitFormRow = {
				type: 'FormRow',
				className: classNames(
					'form__submit',
					!!rememberItem.length && 'form__submit--remember',
				),
				fields: [...rememberItem, newSubmit].filter(Boolean) as IBoxComponent[],
			};
			return newSubmitFormRow;
		}, [
			intl,
			readOnly,
			rememberId,
			storedAuthData,
			rememberDefault,
			newSubmit,
		]);

		const fields = useMemo(
			() => [inputs, submitFormRow],
			[inputs, submitFormRow],
		);

		if (!_items?.length) {
			throw new Error('Missing form items');
		}

		const value = useMemo(
			() => ({
				readOnly,
			}),
			[readOnly],
		);
		return (
			<BoxContextProvider value={value}>
				<form
					onSubmit={(e) => onSubmit?.(e) ?? e.preventDefault()}
					{...formProps}
					className={classNames('form', className)}
				>
					<CustomBox
						prefix={prefix}
						fields={fields}
						// fields={fields}
						destroyValue={destroyValue}
					/>
				</form>
			</BoxContextProvider>
		);
	},
	{ target: 'Form-root', label: 'Form' },
)({
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
	'& .form__submit': {
		borderTop: '1px solid var(--form__submit_border)',
		paddingTop: '2em',
		margin: 0,

		'& button': {
			marginLeft: 'auto',
		},
		'&> .form__row__input': {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
		},
		'& .form__submit--remember': {
			'& .spacer': {
				visibility: 'hidden',
				display: 'inline-block',
				padding: 0,
			},
			'& .spacer + button': {
				float: 'right',
			},
			'& .submit__remember_data': {
				display: 'inline-flex',
				flexShrink: 0,
				margin: 0,
				'& .form__row__input': {
					margin: 0,
				},
				'& .icon': {
					height: '1.5em',
					width: '1.5em',
				},
				'& label': {
					fontWeight: 'bold',
					fontSize: '1.3em',
				},
				marginRight: '2em',
			},
		},
	},
});
